import React from 'react';

import IconApproved from '@mui/icons-material/CheckBox';
import IconDraft from '@mui/icons-material/CheckBoxOutlineBlank';
import IconAllStatuses from '@mui/icons-material/Sort';
import IconDeclined from '@mui/icons-material/Warning';
import {MenuItem, Select, Stack} from '@mui/material';

import {hash53} from '../../utils';

import {
  Project,
  ProjectKeysStatuses,
  ProjectKeysStatusesPerLang,
  ProjectKeyStatus,
  ProjectKeyStatusInfo,
} from '@bumpy/i18n_common';

type KeyStatusBaseProps = {
  lang: string;
  project: Project;
  keyName: string;
  value: string;
  mainLangValue: string;
  onChange: (status: ProjectKeyStatus) => void;
};

type KeyStatusPerLangProps = KeyStatusBaseProps & {
  keysStatusesPerLang: ProjectKeysStatusesPerLang;
};

export const KeyStatusPerLang: React.FC<KeyStatusPerLangProps> = (props) => {
  let status: ProjectKeyStatusInfo | undefined;
  status = props.keysStatusesPerLang[props.lang];

  if (status) {
    if (status.text !== props.value) {
      status = undefined;
    } else if (props.lang === props.project.mainLang) {
      if (props.value !== status.text) {
        status = undefined;
      }
    } else {
      const mainStatus = props.keysStatusesPerLang[props.project.mainLang];
      if (
        !props.mainLangValue ||
        !mainStatus ||
        mainStatus.status !== 'Approved' ||
        status.main_text_hash !== hash53(props.mainLangValue)
      ) {
        status = undefined;
      }
    }
  }

  return <KeyStatusCommon status={status} onChange={props.onChange} />;
};

type KeyStatusPerKeyProps = KeyStatusBaseProps & {
  projectKeysStatuses: ProjectKeysStatuses;
};

export const KeyStatusPerKey: React.FC<KeyStatusPerKeyProps> = (props) => {
  let status: ProjectKeyStatusInfo | undefined;

  const perLang = props.projectKeysStatuses[props.lang];
  if (perLang) {
    status = perLang[props.keyName];
    if (status) {
      if (props.value !== status.text) {
        status = undefined;
      } else {
        const keysStatusesPerLang =
          props.projectKeysStatuses[props.project.mainLang];
        if (keysStatusesPerLang) {
          const mainStatus = keysStatusesPerLang[props.keyName];
          if (
            !props.mainLangValue ||
            !mainStatus ||
            mainStatus.status !== 'Approved' ||
            status.main_text_hash !== hash53(props.mainLangValue)
          ) {
            status = undefined;
          }
        }
      }
    }
  }

  return <KeyStatusCommon status={status} onChange={props.onChange} />;
};

type KeyStatusCommonProps = {
  status?: ProjectKeyStatusInfo;
  onChange: (status: ProjectKeyStatus) => void;
};

const KeyStatusCommon: React.FC<KeyStatusCommonProps> = ({
  status,
  onChange,
}) => {
  if (status) {
    return (
      <Stack direction="row" alignItems="center" spacing={2}>
        <KeyStatusSelector value={status.status} onChange={onChange} />
      </Stack>
    );
  }

  return <KeyStatusSelector value={'Draft'} onChange={onChange} />;
};

type KeyStatusSelectorProps = {
  allStatuses?: boolean;
  value: ProjectKeyStatus;
  onChange: (status: ProjectKeyStatus) => void;
};

export const KeyStatusSelector: React.FC<KeyStatusSelectorProps> = (
  props: KeyStatusSelectorProps,
) => {
  return (
    <Select
      value={props.value}
      variant="standard"
      onChange={(event) => {
        props.onChange(event.target.value as ProjectKeyStatus);
      }}
    >
      {props.allStatuses && (
        <MenuItem value="AllStatuses">
          <Stack direction="row" alignItems="center" spacing={2}>
            <IconAllStatuses color="info" />
            All statuses
          </Stack>
        </MenuItem>
      )}
      <MenuItem value="Draft">
        <Stack direction="row" alignItems="center" spacing={2}>
          <IconDraft color="info" />
          Draft
        </Stack>
      </MenuItem>
      <MenuItem value="Approved">
        <Stack direction="row" alignItems="center" spacing={2}>
          <IconApproved color="success" />
          Approved
        </Stack>
      </MenuItem>
      <MenuItem value="Rejected">
        <Stack direction="row" alignItems="center" spacing={2}>
          <IconDeclined color="error" />
          Declined
        </Stack>
      </MenuItem>
    </Select>
  );
};
